import ParentNode from '@/components/DesignNodes/ParentNode/ParentNode';
import ChildNode from '@/components/DesignNodes/ChildNode/ChildNode';
import GrandChildNode from '@/components/DesignNodes/GrandChildNode/GrandChildNode';
import styles from './DesignMode.module.css';
import { cn } from '@/lib/utils';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { findParent, nodeHasChildren } from '@/lib/functions/designGraph';
import InfoCards from '@/containers/InfoCards/InfoCards';
import Idea from '@/components/widgets/Idea/Idea';
import { Card } from '@/lib/types/constants';
import { useSelector } from 'react-redux';
import { RootState } from '@/redux/store/store';
import { useNavigate, useParams } from 'react-router';
import {
	fetchProposalById,
	processStart,
	processSuccess,
} from '@/redux/slices/proposalSlice';
import { useAppDispatch } from '@/lib/hooks/hooks';
import {
	Document as DocumentType,
	Note as NoteType,
} from '@/lib/types/apiTypes';
import {
	ErrorHandle,
	combineSectionIntoArray,
	findSection,
} from '@/lib/functions/funcUtils';
import { deleteANoteFromASection } from '@/lib/functions/apiCalls';
import { motion, useAnimation } from 'framer-motion';
import DocumentCard from '@/components/widgets/Document/Document';
import { setLastSection } from '@/redux/slices/pageSlice';
import ToolBarInstruction from '@/components/ToolBarInstruction/ToolBarInstruction';
import _ from 'lodash';
import {
	Accordion,
	AccordionContent,
	AccordionItem,
} from '@/components/ui/accordion';
import { AccordionTrigger } from '@radix-ui/react-accordion';
import TopHeader from '@/components/TopHeader/TopHeader';
import DesignModeTitle from '@/components/DesignModeTitle/DesignModeTitle';
import { useTour } from '@/lib/hooks/useTour';
import { Page } from '@/constants/Onboarding/types';

interface DesignModeProps {
	exiting?: boolean;
}

const DesignMode: FC<DesignModeProps> = (props) => {
	const navigate = useNavigate();
	const proposal = useSelector((root: RootState) => root.proposal.proposal);
	const user = useSelector((root: RootState) => root.auth.user);
	const dispatch = useAppDispatch();
	const { id, nodeId } = useParams();
	const [sectionData, setSectionData] = useState<{
		[sectionId: string]: Card[];
	}>({});

	const sortedNodes = useSelector(
		(root: RootState) => root.proposal.sortedNodes
	);

	const [combinedSectionArray, setCombinedSectionArray] = useState<any>([]);

	const [isMacroView, setIsMacroView] = useState<boolean>(true);
	const [activeNodeId, setActiveNodeId] = useState<string | null>(null);
	const tour = useSelector((root: RootState) => root.tour);
	useTour(Page.Design);

	const control = useAnimation();
	const infoCardControl = useAnimation();
	const subCardsControl = useAnimation();
	const topPanelControl = useAnimation();

	const cardsHideAnimation = useMemo(() => {
		return {
			opacity: 0,
			scaleY: 0,
			translateY: '-50%',
			transition: {
				duration: 0.25,
				type: 'cubic-bezier',
				ease: [0.76, 0, 0.24, 1],
			},
		};
	}, []);

	const cardsShowAnimation = useMemo(() => {
		return {
			opacity: 1,
			scaleY: 1,
			translateY: 0,
			transition: {
				duration: 0.4,
				type: 'cubic-bezier',
				ease: [0.76, 0, 0.24, 1],
			},
		};
	}, []);

	const exitingAnimation = { opacity: 0, transition: { duration: 0.25 } };
	const nodeSwitchHide = useCallback(() => {
		infoCardControl.start(cardsHideAnimation);
		subCardsControl.start(cardsHideAnimation);
	}, [infoCardControl, subCardsControl, cardsHideAnimation]);

	const nodeSwitchShow = useCallback(() => {
		infoCardControl.start(cardsShowAnimation);
		subCardsControl.start(cardsShowAnimation);
	}, [infoCardControl, subCardsControl, cardsShowAnimation]);

	const handleNodeClick = (nodeId: string) => {
		setTimeout(() => {
			// setSelectedNode(nodeId);
			setActiveNodeId(nodeId);
			setIsMacroView(false);
			dispatch(
				setLastSection({ proposalId: Number(id), sectionId: Number(nodeId) })
			);

			navigate(`/proposal/${id}/section/${nodeId}/design`);
			nodeSwitchShow();
		}, 250);
		nodeSwitchHide();
	};

	useEffect(() => {
		(async () => {
			if (id !== undefined) {
				dispatch(fetchProposalById(Number(id)));
			}
		})();
	}, [id, dispatch]);

	useEffect(() => {
		if (nodeId) {
			handleNodeClick(nodeId);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [nodeId]);

	useEffect(() => {
		const section = findSection(proposal?.sections || [], nodeId as string);
		if (section) {
			const sortedArray = combineSectionIntoArray(section);
			setCombinedSectionArray(sortedArray);
		}
		setSectionData((prev) => {
			return { ...prev, [nodeId as string]: [] };
		});
	}, [proposal, nodeId]);

	const handleDelete = async (type: string, index: number, isNew?: boolean) => {
		switch (type) {
			case 'idea':
				try {
					setSectionData((prev) => {
						const newNotes = [...prev[nodeId as string]];
						newNotes.splice(index, 1);
						return { ...prev, [nodeId as string]: newNotes };
					});
					if (!isNew) {
						dispatch(processStart());
						const response = await deleteANoteFromASection(
							Number(id),
							Number(nodeId),
							index
						);
						if (response.statusCode === 200) {
							dispatch(processSuccess());
							dispatch(fetchProposalById(Number(id)));
						}
					}
				} catch (error: unknown) {
					dispatch(processSuccess());
					ErrorHandle(dispatch, error);
				}
				break;
			default:
				console.error('Unsupported type:', type);
				break;
		}
	};

	useEffect(() => {
		if (props.exiting === true) {
			infoCardControl.start(exitingAnimation);
			control.start(exitingAnimation);
			subCardsControl.start(exitingAnimation);
			topPanelControl.start(exitingAnimation);
		}
		topPanelControl.start({ opacity: 1, transition: { duration: 0.8 } });
		infoCardControl.start(cardsShowAnimation);
		subCardsControl.start(cardsShowAnimation);
		control.start(cardsShowAnimation);
	}, [props.exiting]);

	return (
		<div className={cn(styles.wrapper, 'bg-[#f5f5f5] ')}>
			<motion.div initial={{ opacity: 0 }} animate={topPanelControl}>
				<TopHeader
					rfpLink={proposal?.rfpLink || ''}
					rfpName={proposal?.rfpTitle || ''}
					proposalName={proposal?.name || ''}
				/>
			</motion.div>
			<div className={cn('grid grid-cols-12 gap-6 px-8')}>
				<motion.div
					className={cn(
						styles.nodeView,
						'col-span-3 pb-4 h-[calc(100vh-160px)] overflow-y-scroll hidden-scroll section-outline'
					)}
					initial={{ opacity: 0, scaleY: 0, translateY: '-50%' }}
					animate={control}
					transition={{
						duration: 0.66,
						delay: 0.12,
						type: 'cubic-bezier',
						ease: [0.76, 0, 0.24, 1],
					}}
					exit={exitingAnimation}
				>
					<Accordion
						type="single"
						// className="w-full"
						defaultValue={findParent(sortedNodes, nodeId as string) as string}
					>
						{sortedNodes?.map((parentNode) => {
							if (parentNode.type === 'parent') {
								const hasChildren = nodeHasChildren(parentNode.id, sortedNodes);
								return (
									<AccordionItem
										value={parentNode.id}
										className="mb-2 border-none"
										key={parentNode.id}
									>
										<AccordionTrigger className="w-full">
											<ParentNode
												key={parentNode.id}
												id={parentNode.id}
												data={parentNode.data}
												hasChildren={hasChildren}
												isSelected={parentNode.id === activeNodeId}
												onClick={() => handleNodeClick(parentNode.id)}
												section={
													sortedNodes.find((node) => node.id === parentNode.id)
														?.data
												}
												editTemplate={user?.templateBuilderAccess}
											/>
										</AccordionTrigger>

										<AccordionContent
											className={cn(
												'w-full mt-2',
												!hasChildren ? 'hidden' : ''
											)}
										>
											{sortedNodes
												.filter((node) => node.data.parentId === parentNode.id)
												.map((childNode) => {
													return (
														<div
															key={childNode.id}
															className="flex flex-col items-end w-full gap-2 mb-2"
														>
															<ChildNode
																key={childNode.id}
																hasChildren={nodeHasChildren(
																	childNode.id,
																	sortedNodes
																)}
																id={childNode.id}
																data={childNode.data}
																isSelected={childNode.id === activeNodeId}
																onClick={() => handleNodeClick(childNode.id)}
																section={
																	sortedNodes.find(
																		(node) => node.id === childNode.id
																	)?.data
																}
																editTemplate={user?.templateBuilderAccess}
															/>
															{/* Render grandchild nodes */}
															{sortedNodes
																.filter(
																	(node) =>
																		node.data.parentId === childNode.id &&
																		node.type === 'grandchild'
																)
																.map((grandchildNode) => (
																	<GrandChildNode
																		hasChildren={nodeHasChildren(
																			grandchildNode.id,
																			sortedNodes
																		)}
																		key={grandchildNode.id}
																		id={grandchildNode.id}
																		data={grandchildNode.data}
																		isSelected={
																			grandchildNode.id === activeNodeId
																		}
																		onClick={() =>
																			handleNodeClick(grandchildNode.id)
																		}
																		section={
																			sortedNodes.find(
																				(node) => node.id === grandchildNode.id
																			)?.data
																		}
																		editTemplate={user?.templateBuilderAccess}
																	/>
																))}
														</div>
													);
												})}
										</AccordionContent>
									</AccordionItem>
								);
							}
						})}
					</Accordion>
				</motion.div>
				<motion.div
					className="col-span-9 "
					initial={{ opacity: 0, scaleY: 0, translateY: '-50%' }}
					animate={infoCardControl}
				>
					<DesignModeTitle
						title={
							sortedNodes?.filter((node) => node.id === activeNodeId)[0]?.data
								.label as string
						}
						setSectionData={setSectionData}
					/>
					<div className="grid grid-cols-12 gap-6 mt-6 ">
						<div
							className={`col-span-7 h-[calc(100vh-250px)] p-3 ${!tour.run ? 'overflow-y-auto' : ''}`}
						>
							<div className="w-full rounded-3xl section-guidelines">
								<InfoCards
									selectedNode={activeNodeId as string}
									sortedNodes={sortedNodes}
								/>
							</div>
						</div>
						<motion.div className={cn('col-span-5 ')} animate={subCardsControl}>
							<div>
								{!isMacroView && (
									<div className="flex flex-col gap-2 h-[calc(100vh-250px)] p-3 overflow-y-auto">
										{(_.isEmpty(combinedSectionArray) ||
											combinedSectionArray.length === 0) &&
										(_.isEmpty(sectionData[nodeId as string]) ||
											sectionData[nodeId as string].length === 0) ? (
											<ToolBarInstruction />
										) : (
											<>
												{(sectionData[nodeId as string] || []).map(
													(card, index) => {
														const commonProps = {
															// key: index,
															onDelete: () =>
																handleDelete(card.type, index, true),
														};
														switch (card.type) {
															case 'idea':
																return (
																	<Idea
																		key={index}
																		{...commonProps}
																		isNew={true}
																		newContent={card.content}
																	/>
																);

															default:
																return null;
														}
													}
												)}

												{combinedSectionArray.map((card, index) => {
													switch (card.type) {
														case 'idea':
															return (
																<Idea
																	key={index}
																	idea={card as NoteType}
																	onDelete={() => handleDelete('idea', card.id)}
																/>
															);
														case 'document':
															return (
																<DocumentCard
																	document={card as DocumentType}
																	user={card.user}
																	key={card.id}
																	onDelete={() =>
																		handleDelete('documents', card.id)
																	}
																/>
															);
													}
												})}
											</>
										)}
									</div>
								)}
							</div>
						</motion.div>
					</div>
				</motion.div>
			</div>
		</div>
	);
};

export default DesignMode;
